import React, { useEffect } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import HotelCard from './components/HotelCard';
import SelectHotelLoading from './components/SelectHotelLoading';
import SelectHotelError from './components/SelectHotelError';
import { useHotelSelection } from '../../hooks/useHotelSelection';

const SelectHotel = () => {
  const {
    hotels,
    loading,
    selectedHotel,
    error,
    isContinuing,
    currentUser,
    loadHotels,
    selectHotel,
    continueWithHotel,
    logout,
    checkAuth
  } = useHotelSelection();

  // Verificar autenticação ao montar o componente
  useEffect(() => {
    if (!checkAuth()) return;
    loadHotels();
  }, [checkAuth, loadHotels]);

  // Loading state
  if (loading) {
    return <SelectHotelLoading />;
  }

  // Error state
  if (error) {
    return (
      <SelectHotelError 
        error={error}
        onRetry={loadHotels}
        onLogout={logout}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Background Pattern Otimizado */}
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Ccircle cx='20' cy='20' r='2'/%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '40px 40px'
        }}
      />

      {/* Main Content */}
      <div className="relative z-10 flex items-center justify-center min-h-screen p-2 sm:p-4">
        <div className="w-full max-w-2xl">
          {/* Card Principal */}
          <div className="bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl shadow-xl p-4 sm:p-6 lg:p-8">
            {/* Header */}
            <div className="text-center mb-6 sm:mb-8">
              <div className="flex items-center justify-center mb-3 sm:mb-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                  <Icon name="Hotel" size={24} color="white" className="sm:hidden" />
                  <Icon name="Hotel" size={28} color="white" className="hidden sm:block" />
                </div>
              </div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">
                Selecione seu Hotel
              </h1>
              <p className="text-slate-600 text-xs sm:text-sm mb-3 sm:mb-4">
                Escolha o hotel que você deseja gerenciar
              </p>
              
              {/* Informação do usuário */}
              {currentUser && (
                <div className="inline-flex items-center gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-slate-100 rounded-lg">
                  <Icon name="User" size={12} color="var(--color-slate-600)" className="sm:hidden" />
                  <Icon name="User" size={14} color="var(--color-slate-600)" className="hidden sm:block" />
                  <span className="text-xs sm:text-sm text-slate-600">
                    {currentUser.name}
                  </span>
                </div>
              )}
            </div>

            {/* Hotel Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
              {hotels.map((hotel) => (
                <HotelCard
                  key={hotel.id}
                  hotel={hotel}
                  isSelected={selectedHotel?.id === hotel.id}
                  onSelect={selectHotel}
                />
              ))}
            </div>

            {/* Continue Button */}
            <div className="text-center">
              <Button
                onClick={continueWithHotel}
                disabled={!selectedHotel || isContinuing}
                variant="default"
                size="lg"
                fullWidth
                loading={isContinuing}
                iconName="ArrowRight"
                iconPosition="right"
              >
                {isContinuing 
                  ? 'Continuando...' 
                  : selectedHotel 
                    ? `Continuar com ${selectedHotel.name}` 
                    : 'Selecione um hotel para continuar'
                }
              </Button>
            </div>

            {/* Logout Option */}
            <div className="text-center mt-4 sm:mt-6">
              <button
                onClick={logout}
                className="text-xs sm:text-sm text-slate-500 hover:text-slate-700 transition-colors"
                disabled={isContinuing}
              >
                Fazer logout
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectHotel;